import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { Icon } from '@/components/icon/index.js';
import { GatorMarkCircle } from './shared/GatorMarkCircle.js';
import { apiPost } from '../lib/api.js';
import { setGateToken } from '../lib/accessGate.js';
import { firebaseSignInWithGoogle, firebaseSignOut, getFirebaseAuth } from '../lib/firebase.js';
import { cn } from '../lib/utils.js';

type Props = {
  onUnlocked: () => void;
};

/**
 * Full-screen tester wall shown on staging/local while the device holds no
 * valid gate token. Renders INSTEAD of the router, so every page — landing
 * included — is behind it. Never shown in production (API reports disabled).
 */
export function AccessGate({ onUnlocked }: Props) {
  const { t } = useTranslation();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'credentials' | 'google' | null>(null);

  const unlock = (token: string) => {
    setGateToken(token);
    onUnlocked();
  };

  const submitCredentials = async () => {
    if (!login.trim() || !password || busy) return;
    setBusy('credentials');
    setError(null);
    try {
      const res = await apiPost<{ token: string }>(
        '/access-gate/login',
        { login: login.trim(), password },
        { suppressErrorToast: true },
      );
      unlock(res.token);
    } catch (err) {
      const code = (err as AxiosError<{ code?: string }>).response?.data?.code;
      setError(
        code === 'INVALID_CREDENTIALS'
          ? t('accessGate.invalidCredentials', { defaultValue: 'Invalid login or password.' })
          : t('accessGate.error', { defaultValue: 'Something went wrong — try again.' }),
      );
    } finally {
      setBusy(null);
    }
  };

  const submitGoogle = async () => {
    if (busy) return;
    setBusy('google');
    setError(null);
    try {
      await firebaseSignInWithGoogle();
      const idToken = await getFirebaseAuth().currentUser?.getIdToken();
      if (!idToken) throw new Error('No Google session');
      const res = await apiPost<{ token: string }>(
        '/access-gate/google',
        { idToken },
        { suppressErrorToast: true },
      );
      unlock(res.token);
    } catch (err) {
      const code = (err as AxiosError<{ code?: string }>).response?.data?.code;
      if (code === 'NOT_ALLOWED') {
        // Don't leave a half-authorized Firebase session behind.
        await firebaseSignOut().catch(() => undefined);
        setError(
          t('accessGate.googleNotAllowed', {
            defaultValue: 'This Google account does not have tester access.',
          }),
        );
      } else {
        setError(t('accessGate.error', { defaultValue: 'Something went wrong — try again.' }));
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-ig-bg-primary px-5 py-12 text-ig-text-primary">
      {/* Accent glow — same treatment as the welcome wizard. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-180px] h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-ig-accent opacity-[0.07] blur-3xl"
      />

      <div className="relative w-full max-w-[420px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <GatorMarkCircle className="h-16 w-16" alt="" />
          <span className="inline-flex items-center gap-1.5 rounded-full border border-ig-warning/40 bg-ig-warning/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-ig-warning">
            <Icon name="lock" className="h-3 w-3" aria-hidden />
            {t('accessGate.badge', { defaultValue: 'Private preview' })}
          </span>
          <h1 className="font-display text-[32px] font-light leading-tight md:text-[40px]">
            {t('accessGate.title', { defaultValue: 'Tester access only' })}
          </h1>
          <p className="max-w-[340px] font-sans text-[14px] leading-[1.65] text-ig-text-secondary">
            {t('accessGate.subtitle', {
              defaultValue: 'This environment is a private preview of InterviewGator. Sign in with your tester credentials to continue.',
            })}
          </p>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg border border-ig-border bg-ig-bg-secondary">
          <div className="h-[3px] w-full" style={{ background: 'linear-gradient(90deg, #128A3E, #1DB954 45%, #6EE7A0)' }} />
          <form
            className="space-y-4 p-6"
            onSubmit={(e) => {
              e.preventDefault();
              void submitCredentials();
            }}
          >
            <label className="block space-y-1.5">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.136em] text-ig-text-tertiary">
                {t('accessGate.login', { defaultValue: 'Login' })}
              </span>
              <input
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                autoComplete="username"
                autoFocus
                className="h-12 w-full rounded-lg border border-ig-border bg-ig-bg-primary px-4 text-[15px] text-ig-text-primary outline-none transition focus:border-ig-accent"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.136em] text-ig-text-tertiary">
                {t('accessGate.password', { defaultValue: 'Password' })}
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="h-12 w-full rounded-lg border border-ig-border bg-ig-bg-primary px-4 text-[15px] text-ig-text-primary outline-none transition focus:border-ig-accent"
              />
            </label>

            {error ? <p className="text-[13px] text-ig-danger">{error}</p> : null}

            <button
              type="submit"
              disabled={!login.trim() || !password || busy !== null}
              className={cn(
                'inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg font-sans text-[15px] font-semibold transition',
                !login.trim() || !password || busy !== null
                  ? 'cursor-not-allowed bg-ig-bg-tertiary text-ig-text-tertiary'
                  : 'bg-ig-accent text-[var(--ig-accent-text)] hover:brightness-110',
              )}
            >
              {busy === 'credentials' ? <Icon name="loader-2" className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {t('accessGate.submit', { defaultValue: 'Enter preview' })}
              <Icon name="arrow-right" className="h-4 w-4" aria-hidden />
            </button>

            <div className="flex items-center gap-3" aria-hidden>
              <span className="h-px flex-1 bg-ig-border" />
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ig-text-tertiary">
                {t('accessGate.or', { defaultValue: 'or' })}
              </span>
              <span className="h-px flex-1 bg-ig-border" />
            </div>

            <button
              type="button"
              onClick={() => void submitGoogle()}
              disabled={busy !== null}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-ig-border bg-ig-bg-primary font-sans text-[14px] font-medium text-ig-text-primary transition hover:border-ig-border-strong disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy === 'google' ? (
                <Icon name="loader-2" className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
              )}
              {t('accessGate.google', { defaultValue: 'Continue with Google' })}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center font-sans text-[12px] text-ig-text-tertiary">
          {t('accessGate.footer', { defaultValue: 'Access is granted by the InterviewGator team.' })}
        </p>
      </div>
    </div>
  );
}
